import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Home, Send, Loader2, ExternalLink } from "lucide-react";
import SimulatedBanner from "@/components/SimulatedBanner";
import PhotoUploader from "@/components/PhotoUploader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const PROPERTY_TYPES = [
  { key: "casa", label: "Casa" },
  { key: "apartamento", label: "Apartamento" },
  { key: "terreno", label: "Terreno" },
  { key: "local", label: "Local comercial" },
  { key: "oficina", label: "Oficina" },
];

const STATUS_LABEL = {
  pendiente_aprobacion: "Pendiente",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
  publicada: "Publicada",
};

export default function MercadoLibrePage() {
  const [listings, setListings] = useState([]);
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "", description: "", price: "", currency: "USD",
    property_type: "apartamento", rooms: 2, bathrooms: 2, surface_m2: 80,
    city: "", neighborhood: "",
  });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.get("/mercadolibre/listings"),
        api.get("/mercadolibre/connection"),
      ]);
      setListings(l.data);
      setConn(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 5) return toast.error("Título muy corto");
    if (!form.price) return toast.error("Precio requerido");
    if (photos.length === 0) return toast.error("Debe subir al menos 1 foto del inmueble");
    setSending(true);
    try {
      await api.post("/mercadolibre/listings", {
        ...form, price: parseFloat(form.price),
        rooms: parseInt(form.rooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        surface_m2: parseFloat(form.surface_m2) || 0,
        photos,
      });
      toast.success("Aviso agregado");
      setForm({ ...form, title: "", description: "", price: "" });
      setPhotos([]);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const decide = async (listing, action) => {
    try {
      await api.post(`/mercadolibre/listings/${listing.id}/decision`, { action });
      toast.success(action === "publicar" ? "Publicado" : action);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="mercadolibre-page">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1">
          <div className="h-1 w-16 bg-brand-aqua mb-4 rounded-full" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">MercadoLibre Venezuela</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            <span>Publicación de avisos inmobiliarios (site MLV) vía API oficial.</span>
          </p>
        </div>
        {!conn?.connected ? (
          <SimulatedBanner testid="ml-simulated-banner" />
        ) : (
          <Badge className="bg-brand-aqua-soft text-brand-aqua-dark border-brand-aqua/40 font-semibold">
            <span>Conectada · user {conn.ml_user_id}</span>
          </Badge>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="card-elevated p-6 space-y-4" data-testid="ml-composer">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Apartamento moderno 2H/2B — Las Mercedes" data-testid="ml-title" /></div>
          <div><Label>Tipo de inmueble</Label>
            <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
              <SelectTrigger data-testid="ml-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROPERTY_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="ml-desc" /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><Label>Precio</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="ml-price" /></div>
          <div><Label>Moneda</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} maxLength={5} /></div>
          <div><Label>Habitaciones</Label><Input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} /></div>
          <div><Label>Baños</Label><Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>m²</Label><Input type="number" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} /></div>
          <div><Label>Ciudad</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Caracas" /></div>
          <div><Label>Zona / Barrio</Label><Input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} /></div>
        </div>
        <div>
          <Label>Fotos del inmueble <span className="text-destructive">*</span></Label>
          <p className="text-xs text-stone-500 mb-2">Sube al menos 1 foto. Máx 3 MB por foto. Formatos: JPG, PNG, WEBP.</p>
          <PhotoUploader
            endpoint="/mercadolibre/photos"
            value={photos}
            onChange={setPhotos}
            testid="ml-photos"
          />
        </div>
        <Button type="submit" disabled={sending} className="btn-primary" data-testid="ml-submit">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Home className="h-4 w-4 mr-2" />}
          Crear aviso
        </Button>
      </form>

      {/* Listings */}
      <div className="card-elevated overflow-hidden" data-testid="ml-listings">
        <div className="p-4 border-b border-border font-semibold text-brand-ink">Avisos ({listings.length})</div>
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
        ) : (
          <div className="divide-y divide-border">
            {listings.map((l) => (
              <div key={l.id} className="p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`ml-listing-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-brand-ink"><span>{l.title}</span></div>
                  <div className="text-xs text-stone-500 mt-1 flex flex-wrap gap-2">
                    <span>{l.property_type}</span><span>·</span>
                    <span>{l.rooms}H / {l.bathrooms}B</span><span>·</span>
                    <span>{l.surface_m2}m²</span><span>·</span>
                    <span>{l.currency} {l.price?.toFixed(2)}</span>
                  </div>
                  {l.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`ml-listing-photos-${l.id}`}>
                      {l.photos.slice(0, 5).map((url, i) => (
                        <img key={i} src={absUrl(url)} alt="foto" className="h-14 w-14 rounded-md object-cover border border-border" />
                      ))}
                      {l.photos.length > 5 && (
                        <div className="h-14 w-14 rounded-md border border-border flex items-center justify-center text-xs text-stone-500 bg-stone-50">
                          +{l.photos.length - 5}
                        </div>
                      )}
                    </div>
                  )}
                  {l.ml_permalink && (
                    <a href={l.ml_permalink} target="_blank" rel="noreferrer" className="text-xs text-brand-emerald-dark hover:underline inline-flex items-center gap-1 mt-1">
                      Ver aviso <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Badge className="bg-stone-100 text-stone-600 border-stone-200 text-[10px]"><span>{STATUS_LABEL[l.status] || l.status}</span></Badge>
                  {l.status === "pendiente_aprobacion" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decide(l, "aprobar")} data-testid={`ml-approve-${l.id}`}>Aprobar</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(l, "rechazar")}>Rechazar</Button>
                    </>
                  )}
                  {l.status === "aprobada" && (
                    <Button size="sm" className="btn-primary" onClick={() => decide(l, "publicar")} data-testid={`ml-publish-${l.id}`}>Publicar</Button>
                  )}
                </div>
              </div>
            ))}
            {listings.length === 0 && <div className="p-10 text-center text-stone-500 text-sm">Sin avisos aún.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
